import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import { BudgetExpenseOrIncome } from 'business/constants';
import WithLoader from 'ui/WithLoader/WithLoader';
import {
    BudgetProposalT,
    ResourceBudgetProposalAssociationT,
    ResourceNameField,
} from 'routes/BudgetModule/config/types';
import { Box } from '@mui/material';
import NewInscriptionModal from './NewInscriptionModal';
import { AddInscriptionModalTitles } from './types';
import { InscriptionQueryKey } from 'routes/BudgetModule/helpers/createBudgetQueryKeys';

type Props = {
    stepTitles: AddInscriptionModalTitles;
    budgetProposalResources: ResourceBudgetProposalAssociationT[];
    resourceType: ResourceNameField;
    expenseOrIncome: BudgetExpenseOrIncome;
    showOperationColumn?: boolean;
    allowOrphanInscription: boolean;
    operationSoftwareId?: string;
    isOperationDraft?: boolean;
    onCloseModal?: (
        isOrphan?: boolean,
        budgetProposal?: BudgetProposalT,
        resourceName?: string,
    ) => void;
    inscriptionQueryCacheKey: InscriptionQueryKey;
    isLoading?: boolean;
};

function AddInscription({
    stepTitles,
    budgetProposalResources,
    resourceType,
    expenseOrIncome,
    showOperationColumn = false,
    allowOrphanInscription,
    operationSoftwareId = undefined,
    isOperationDraft = false,
    onCloseModal = undefined,
    inscriptionQueryCacheKey,
    isLoading = false,
}: Props): React.ReactElement {
    const { t } = useTranslation();

    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

    const onClose = (
        isOrphan: boolean,
        budgetProposal: BudgetProposalT,
        resourceName: string,
    ) => {
        setIsModalOpen(false);
        if (!onCloseModal) return;
        onCloseModal(isOrphan, budgetProposal, resourceName);
    };

    return (
        <Box sx={{ marginTop: '1em' }}>
            <WithLoader isLoading={isLoading} color="secondary">
                <Button
                    variant="contained"
                    size="small"
                    sx={{ minHeight: '1.75rem', marginLeft: '1.5rem' }}
                    onClick={() => setIsModalOpen(true)}
                    startIcon={<AddIcon fontSize="small" />}
                    disabled={budgetProposalResources.length === 0}
                >
                    <Typography
                        sx={{ fontSize: '11.5px', color: 'inherit' }}
                        variant="body1SemiBold"
                    >
                        {t('budgetProposal.addInscription')}
                    </Typography>
                </Button>
            </WithLoader>
            <NewInscriptionModal
                stepTitles={stepTitles}
                isOpen={isModalOpen}
                onClose={onClose}
                budgetProposalResources={budgetProposalResources.filter(
                    (budgetProposalResource) =>
                        budgetProposalResource.canAddInscription,
                )}
                resourceType={resourceType}
                expenseOrIncome={expenseOrIncome}
                showOperationColumn={showOperationColumn}
                allowOrphanInscription={allowOrphanInscription}
                operationSoftwareId={operationSoftwareId}
                isOperationDraft={isOperationDraft}
                inscriptionQueryCacheKey={inscriptionQueryCacheKey}
            />
        </Box>
    );
}
export default AddInscription;
